import { useState, useEffect } from 'react';
import { getImageUrl } from '../../services/imageService';
import './Image.css';

/**
 * Reusable Image Component
 * Automatically handles base URL construction for relative image paths
 * 
 * @param {string} src - Image source (relative path or full URL)
 * @param {string} alt - Alt text for the image
 * @param {string} className - Additional CSS classes
 * @param {string} fallback - Fallback image path (optional)
 * @param {Function} onError - Custom error handler (optional)
 * @param {Object} ...props - Other img element props
 */
const Image = ({ 
  src, 
  alt = '', 
  className = '', 
  fallback = null,
  onError,
  ...props 
}) => {
  const [imageSrc, setImageSrc] = useState(() => {
    if (!src) return fallback || null;
    
    // Convert to string and trim
    const srcStr = String(src).trim();
    
    // If src is already a full URL (starts with http:// or https://), use it directly
    if (srcStr.match(/^https?:\/\//i)) {
      return srcStr;
    }
    
    // Process relative paths through getImageUrl
    const processedUrl = getImageUrl(srcStr);
    
    // Double-check: if processed URL still looks wrong (has localhost in front of https), fix it
    if (processedUrl && processedUrl.includes('localhost') && processedUrl.includes('https://')) {
      const fixedUrl = processedUrl.replace(/^https?:\/\/[^/]+/, '').replace(/^\/+/, '');
      return fixedUrl;
    }
    
    return processedUrl || fallback || null;
  });
  const [hasError, setHasError] = useState(false);

  // Keep internal image source in sync with `src` and `fallback` props
  useEffect(() => {
    if (!src) {
      setImageSrc(fallback || null);
      setHasError(false);
      return;
    }

    const srcStr = String(src).trim();

    // If src is already a full URL (starts with http:// or https://), use it directly
    if (srcStr.match(/^https?:\/\//i)) {
      setImageSrc(srcStr);
      setHasError(false);
      return;
    }

    // Process relative paths through getImageUrl
    const processedUrl = getImageUrl(srcStr);

    // Double-check: if processed URL still looks wrong (has localhost in front of https), fix it
    if (processedUrl && processedUrl.includes('localhost') && processedUrl.includes('https://')) {
      const fixedUrl = processedUrl.replace(/^https?:\/\/[^/]+/, '').replace(/^\/+/, '');
      setImageSrc(fixedUrl);
      setHasError(false);
      return;
    }

    setImageSrc(processedUrl || fallback || null);
    setHasError(false);
  }, [src, fallback]);

  const handleError = (e) => {
    setHasError(true);
    
    // Try fallback if available and not already using it
    if (fallback && imageSrc !== fallback) {
      setImageSrc(fallback);
      return;
    }
    
    // Call custom error handler if provided
    if (onError) {
      onError(e);
    }
  };

  // Don't render if no source and no fallback
  if (!imageSrc && !fallback) {
    return (
      <div className={`image-placeholder ${className}`} aria-label={alt}>
        <span>No Image</span>
      </div>
    );
  }

  // If error occurred and no fallback, show placeholder
  if (hasError && !fallback) {
    return (
      <div className={`image-placeholder ${className}`} aria-label={alt}>
        <span>Image Not Available</span>
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      onError={handleError}
      {...props}
    />
  );
};

export default Image;

